import React from 'react';
import { Link } from 'react-router-dom';
import {
    Eye as EyeIcon,
    FileEdit as FileEditIcon,
    FilePlus as FilePlusIcon,
    ChartColumn as ChartIcon,
    FileX as FileXIcon,
    Pencil as PencilIcon,
} from 'lucide-react';
import RowIconAction, { iconActionClassName } from 'src/app/admin/components/row-icon-action/RowIconAction';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { ProjectViews } from 'src/routes/routesConfig';
import { ProgressStatus } from 'src/shared/types';
import { MergedProjectRow } from '../../../../mergeProjectsWithDefinitions';

type Act = (row: MergedProjectRow) => void;

/** Names the project in a way that stays unambiguous in a list of them. */
const labelFor = (row: MergedProjectRow) => row.projectName ?? row.projectId;

/**
 * The row itself opens the project's definition, so an action must stop the click reaching it —
 * otherwise both run and the row wins.
 */
const only =
    (act: Act, row: MergedProjectRow) =>
    (event: React.MouseEvent): void => {
        event.stopPropagation();
        act(row);
    };

const RenameAction = ({ row, onRename }: { row: MergedProjectRow; onRename: Act }) => (
    <RowIconAction label='Rename project'>
        <button
            type='button'
            aria-label={`Rename ${labelFor(row)}`}
            data-testid={`project-rename-${row.projectId}`}
            className={iconActionClassName}
            onClick={only(onRename, row)}
        >
            <PencilIcon size={16} className='shrink-0' />
        </button>
    </RowIconAction>
);

const StatsAction = ({ row, onShowStats }: { row: MergedProjectRow; onShowStats: Act }) => (
    <RowIconAction label='Project stats'>
        <button
            type='button'
            aria-label={`Stats for ${labelFor(row)}`}
            data-testid={`project-stats-${row.projectId}`}
            className={iconActionClassName}
            onClick={only(onShowStats, row)}
        >
            <ChartIcon size={16} className='shrink-0' />
        </button>
    </RowIconAction>
);

const AddDefinitionAction = ({ row, onStartDefinition }: { row: MergedProjectRow; onStartDefinition: Act }) => (
    <RowIconAction label='Add definition'>
        <button
            type='button'
            aria-label={`Add definition for ${labelFor(row)}`}
            data-testid={`project-add-definition-${row.projectId}`}
            className={iconActionClassName}
            onClick={only(onStartDefinition, row)}
        >
            <FilePlusIcon size={16} className='shrink-0' />
        </button>
    </RowIconAction>
);

const ResumeDefinitionAction = ({ row, onStartDefinition }: { row: MergedProjectRow; onStartDefinition: Act }) => (
    <RowIconAction label='Resume definition'>
        <button
            type='button'
            aria-label={`Resume definition for ${labelFor(row)}`}
            data-testid={`project-resume-definition-${row.projectId}`}
            className={iconActionClassName}
            onClick={only(onStartDefinition, row)}
        >
            <FileEditIcon size={16} className='shrink-0' />
        </button>
    </RowIconAction>
);

/** Once a definition is complete there is a project to look at, as a site card under My Sites opens. */
const ViewProjectAction = ({ row, customerId }: { row: MergedProjectRow; customerId: string }) => (
    <RowIconAction label='View project'>
        <Link
            to={navRoutesConfig[ProjectViews.SITE_SUMMARY]({ customerId, projectId: row.projectId })}
            aria-label={`View project ${labelFor(row)}`}
            data-testid={`project-view-${row.projectId}`}
            className={iconActionClassName}
            onClick={event => event.stopPropagation()}
        >
            <EyeIcon size={16} className='shrink-0' />
        </Link>
    </RowIconAction>
);

const DeleteDefinitionAction = ({ row, onDeleteDefinition }: { row: MergedProjectRow; onDeleteDefinition: Act }) => (
    <RowIconAction label='Delete definition'>
        <button
            type='button'
            aria-label={`Delete definition for ${labelFor(row)}`}
            data-testid={`project-delete-definition-${row.projectId}`}
            className={iconActionClassName}
            onClick={only(onDeleteDefinition, row)}
        >
            <FileXIcon size={16} className='shrink-0' />
        </button>
    </RowIconAction>
);

type DefinitionActionsProps = {
    row: MergedProjectRow;
    customerId: string;
    canAdminister: boolean;
    onStartDefinition: Act;
    onDeleteDefinition: Act;
};

/**
 * Everything to do with a project's definition, gathered in one place: the one action its state
 * calls for, plus deleting it where there is one to delete. Adding or resuming enters the wizard,
 * which reads its prefill from redux rather than the url.
 */
const DefinitionActions = ({ row, customerId, canAdminister, onStartDefinition, onDeleteDefinition }: DefinitionActionsProps) => {
    if (!row.hasDefinition) {
        return <AddDefinitionAction row={row} onStartDefinition={onStartDefinition} />;
    }

    return (
        <>
            {row.definition?.status === ProgressStatus.PENDING ? (
                <ResumeDefinitionAction row={row} onStartDefinition={onStartDefinition} />
            ) : (
                <ViewProjectAction row={row} customerId={customerId} />
            )}

            {/* Trash is reserved for deleting the project itself. */}
            {canAdminister && <DeleteDefinitionAction row={row} onDeleteDefinition={onDeleteDefinition} />}
        </>
    );
};

type ProjectRowActionsProps = {
    row: MergedProjectRow;
    customerId: string;
    /** Whether this user may perform the mutations, which the admin-only actions are gated on. */
    canAdminister: boolean;
    onRename: Act;
    onShowStats: Act;
    onStartDefinition: Act;
    onDeleteDefinition: Act;
};

/** What can be done to one project from its row. */
const ProjectRowActions = ({
    row,
    customerId,
    canAdminister,
    onRename,
    onShowStats,
    onStartDefinition,
    onDeleteDefinition,
}: ProjectRowActionsProps) => (
    <div className='flex items-center gap-1 shrink-0'>
        {canAdminister && <RenameAction row={row} onRename={onRename} />}
        <StatsAction row={row} onShowStats={onShowStats} />
        <DefinitionActions
            row={row}
            customerId={customerId}
            canAdminister={canAdminister}
            onStartDefinition={onStartDefinition}
            onDeleteDefinition={onDeleteDefinition}
        />
    </div>
);

export default ProjectRowActions;
